import { useCallback } from 'react'
import { useDrop } from 'react-dnd'
import styled from 'styled-components'
import { LEGAL_COPYRIGHT_NOTICE } from '../../constants'
import { useKeyboardShortcut, usePreviewMode } from '../../hooks'
import { useGetUserInfo } from '../../services/api/user-info.hooks'
import { Border, Colors, Dimensions, Shadows, Spacing } from '../../styles'
import { NAVIGATION_BAR_COLLAPSED_WIDTH, NAVIGATION_BAR_WIDTH } from '../../styles/dimensions'
import { icons } from '../../styles/images'
import { Motion, fadeIn } from '../../styles/motion'
import { DropType } from '../../utils/types'
import Flex from '../atoms/Flex'
import GTButton from '../atoms/buttons/GTButton'
import NoStyleButton from '../atoms/buttons/NoStyleButton'
import { BodySmallUpper } from '../atoms/typography/Typography'
import CommandPalette from '../molecules/CommandPalette'
import SettingsModalButton from '../molecules/SettingsModalButton'
import ShareFeedbackButton from '../molecules/ShareFeedbackButton'
import TrialIndicator from '../molecules/TrialIndicator'
import NavigationSectionLinks from '../navigation_sidebar/NavigationSectionLinks'
import ShellLogo from '../navigation_sidebar/ShellLogo'
import NoteCreateButton from '../notes/NoteCreateButton'
import StreakPopover from '../streaks/StreakPopover'
import NavigationViewCollapsed from './NavigationViewCollapsed'

const NavigationViewContainer = styled.div<{ showDropShadow: boolean; isCollapsed: boolean }>`
    display: flex;
    flex-direction: column;
    min-width: 0px;
    min-height: 0px;
    height: 100%;
    background-color: ${Colors.background.panel};
    border-right: ${Border.stroke.medium} solid ${Colors.background.border};
    box-sizing: border-box;
    z-index: 1;
    ${(props) => props.showDropShadow && `box-shadow: ${Shadows.button.secondary};`}
    width: ${({ isCollapsed }) => (isCollapsed ? NAVIGATION_BAR_COLLAPSED_WIDTH : NAVIGATION_BAR_WIDTH)};
    /* Opens and closes by width; the contents are clipped while it moves rather than reflowing. */
    overflow: hidden;
    transition: width ${Motion.panel};

    ${Dimensions.mediaQuery.phone} {
        width: 100%;
    }
`
// Holds the rail or the full nav at its final width, so neither squeezes while the panel animates.
const NavigationContent = styled.div<{ isCollapsed: boolean }>`
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    height: 100%;
    width: calc(
        ${({ isCollapsed }) => (isCollapsed ? NAVIGATION_BAR_COLLAPSED_WIDTH : NAVIGATION_BAR_WIDTH)} -
            ${Border.stroke.medium}
    );
    animation: ${fadeIn} ${Motion.enter};

    ${Dimensions.mediaQuery.phone} {
        width: 100%;
    }
`
const NavigationViewHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-basis: 24px;
    width: 100%;
    margin-top: ${Spacing._8};
    margin-bottom: ${Spacing._12};
    padding: ${Spacing._16} ${Spacing._16} 0;
    box-sizing: border-box;
`
const OverflowContainer = styled.div`
    flex: 1;
    margin-bottom: ${Spacing._8};
    display: flex;
    flex-direction: column;
    overflow: auto;
    padding: 0 ${Spacing._8};
`
const GapView = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${Spacing._8};
    padding-bottom: ${Spacing._8};
    margin-top: auto;
    padding: 0 ${Spacing._16};
`
const CopyrightText = styled.span`
    margin-top: ${Spacing._4};
    text-align: center;
    user-select: none;
    padding: ${Spacing._16};
`
interface NavigationViewProps {
    isCollapsed: boolean
    setIsCollapsed: (isCollapsed: boolean) => void
    hideCollapseControl?: boolean
    hideCommandPalette?: boolean
}
const NavigationView = ({
    isCollapsed,
    setIsCollapsed,
    hideCollapseControl = false,
    hideCommandPalette = false,
}: NavigationViewProps) => {
    const { data: userInfo } = useGetUserInfo()
    const { isPreviewMode, toggle: togglePreviewMode } = usePreviewMode()

    const [isOver, drop] = useDrop(
        () => ({
            accept: [DropType.TASK],
            collect: (monitor) => monitor.isOver(),
        }),
        []
    )
    useKeyboardShortcut(
        'navigationView',
        useCallback(() => {
            setIsCollapsed(!isCollapsed)
        }, [isCollapsed])
    )

    return (
        <NavigationViewContainer showDropShadow={isOver} ref={drop} isCollapsed={isCollapsed}>
            <NavigationContent key={isCollapsed ? 'rail' : 'full'} isCollapsed={isCollapsed}>
                {isCollapsed ? (
                    <NavigationViewCollapsed setIsCollapsed={setIsCollapsed} />
                ) : (
                    <>
                        <NavigationViewHeader>
                            <ShellLogo />
                            <Flex gap={Spacing._4}>
                                {isPreviewMode && <StreakPopover />}
                                {!hideCollapseControl && (
                                    <GTButton
                                        styleType="icon"
                                        icon={icons.sidebar}
                                        onClick={() => setIsCollapsed(!isCollapsed)}
                                        shortcutName="navigationView"
                                    />
                                )}
                                <NoteCreateButton type="icon" />
                                {!hideCommandPalette && <CommandPalette />}
                            </Flex>
                        </NavigationViewHeader>
                        <OverflowContainer>
                            <NavigationSectionLinks />
                        </OverflowContainer>
                        <GapView>
                            <TrialIndicator />
                            <ShareFeedbackButton />
                            <SettingsModalButton type="nav-button" />
                        </GapView>
                        <CopyrightText>
                            {userInfo?.is_employee ? (
                                <NoStyleButton onClick={() => togglePreviewMode()}>
                                    <BodySmallUpper color={isPreviewMode ? 'ink' : 'light'}>
                                        {isPreviewMode ? '© 2026 GENERAL KENOBI' : LEGAL_COPYRIGHT_NOTICE}
                                    </BodySmallUpper>
                                </NoStyleButton>
                            ) : (
                                <BodySmallUpper color="light">{LEGAL_COPYRIGHT_NOTICE}</BodySmallUpper>
                            )}
                        </CopyrightText>
                    </>
                )}
            </NavigationContent>
        </NavigationViewContainer>
    )
}

export default NavigationView
